import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Statistic, Table, Tag } from 'antd';
import {
  UserOutlined,
  PictureOutlined,
  TagsOutlined,
  CrownOutlined,
} from '@ant-design/icons';
import { getUserList, getStickerList, getCategoryList } from '../services/api';
import dayjs from 'dayjs';

const Dashboard = () => {
  const [stats, setStats] = useState({
    userCount: 0,
    vipCount: 0,
    stickerCount: 0,
    categoryCount: 0,
  });
  const [recentUsers, setRecentUsers] = useState([]);
  const [recentStickers, setRecentStickers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [usersData, stickersData, categoriesData] = await Promise.all([
        getUserList({ page: 1, page_size: 5 }),
        getStickerList({ page: 1, page_size: 5 }),
        getCategoryList(),
      ]);

      setStats({
        userCount: usersData.total || 0,
        vipCount: 0, // 需要额外统计
        stickerCount: stickersData.total || 0,
        categoryCount: categoriesData?.length || 0,
      });

      setRecentUsers(usersData.list || []);
      setRecentStickers(stickersData.list || []);
    } catch (error) {
      console.error('加载数据失败', error);
    } finally {
      setLoading(false);
    }
  };

  const userColumns = [
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 1 ? 'green' : 'red'}>
          {status === 1 ? '正常' : '禁用'}
        </Tag>
      ),
    },
    {
      title: '会员',
      dataIndex: 'is_vip',
      key: 'is_vip',
      render: (isVip) => (
        isVip ? <Tag color="gold">VIP</Tag> : <Tag>普通</Tag>
      ),
    },
    {
      title: '注册时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text) => dayjs(text).format('YYYY-MM-DD HH:mm'),
    },
  ];

  const stickerColumns = [
    {
      title: '预览',
      dataIndex: 'thumbnail_url',
      key: 'thumbnail_url',
      width: 80,
      render: (url, record) => (
        <img
          src={url || record.file_url}
          alt={record.name}
          style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 8 }}
        />
      ),
    },
    {
      title: '名称',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
    },
    {
      title: '类型',
      dataIndex: 'file_type',
      key: 'file_type',
      render: (type) => (
        <Tag color={type === 'animated' ? 'purple' : 'blue'}>
          {type === 'animated' ? '动图' : '静态'}
        </Tag>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text) => dayjs(text).format('YYYY-MM-DD HH:mm'),
    },
  ];

  const statCards = [
    {
      title: '总用户数',
      value: stats.userCount,
      icon: <UserOutlined />,
      color: '#667eea',
      bgColor: 'rgba(102, 126, 234, 0.1)',
    },
    {
      title: 'VIP用户',
      value: stats.vipCount,
      icon: <CrownOutlined />,
      color: '#f5a623',
      bgColor: 'rgba(245, 166, 35, 0.1)',
    },
    {
      title: '贴纸总数',
      value: stats.stickerCount,
      icon: <PictureOutlined />,
      color: '#52c41a',
      bgColor: 'rgba(82, 196, 26, 0.1)',
    },
    {
      title: '分类数量',
      value: stats.categoryCount,
      icon: <TagsOutlined />,
      color: '#eb2f96',
      bgColor: 'rgba(235, 47, 150, 0.1)',
    },
  ];

  return (
    <div className="page-container">
      <div className="page-header">
        <h2>仪表盘</h2>
        <p>欢迎回来，这是系统概览</p>
      </div>

      <Row gutter={[24, 24]}>
        {statCards.map((card, index) => (
          <Col xs={24} sm={12} lg={6} key={index}>
            <div className="stat-card">
              <div
                className="stat-icon"
                style={{ background: card.bgColor, color: card.color }}
              >
                {card.icon}
              </div>
              <Statistic
                title={card.title}
                value={card.value}
                valueStyle={{ color: card.color, fontWeight: 700 }}
              />
            </div>
          </Col>
        ))}
      </Row>

      <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
        <Col xs={24} lg={12}>
          <Card title="最近注册用户" bordered={false}>
            <Table
              columns={userColumns}
              dataSource={recentUsers}
              rowKey="id"
              pagination={false}
              loading={loading}
              size="small"
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="最近添加贴纸" bordered={false}>
            <Table
              columns={stickerColumns}
              dataSource={recentStickers}
              rowKey="id"
              pagination={false}
              loading={loading}
              size="small"
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;

